/**
 * Google Maps Marker Icons Configuration
 * 
 * Custom SVG icons located in /public/markers/
 */

export interface MapIconConfig {
  url: string;
  scaledSize?: { width: number; height: number };
  anchor?: { x: number; y: number };
  size?: { width: number; height: number };
}

/**
 * Map icon configurations for different shop types
 */
export const mapIcons: Record<string, MapIconConfig> = {
  // Kringloop only
  kringloop: {
    url: "/markers/Kringloop.svg",
    scaledSize: { width: 96, height: 96 },
    anchor: { x: 48, y: 72 }, // Adjusted from 96 to 72 to account for bottom margin in SVG
  },

  // Kringloop favorite
  kringloop_favorite: {
    url: "/markers/Kringloop Favorite.svg",
    scaledSize: { width: 96, height: 96 },
    anchor: { x: 48, y: 72 },
  },

  // Kringloop Horeca
  kringloop_horeca: {
    url: "/markers/Kringloop Horeca.svg",
    scaledSize: { width: 96, height: 96 },
    anchor: { x: 48, y: 72 },
  },

  // Kringloop Horeca favorite
  kringloop_horeca_favorite: {
    url: "/markers/Kringloop Horeca Favorite.svg",
    scaledSize: { width: 96, height: 96 },
    anchor: { x: 48, y: 72 },
  },

  // Default fallback
  default: {
    url: "/markers/Kringloop.svg",
    scaledSize: { width: 96, height: 96 },
    anchor: { x: 48, y: 72 },
  },
};

/**
 * Determine shop type based on shop properties
 * 
 * @param shop - Shop object with has_charity, has_horeca, and potentially favorite status
 * @returns Icon key for mapIcons
 */
export function getShopIconKey(shop: {
  has_charity?: boolean | null;
  has_horeca?: boolean | null;
  name?: string;
  isFavorite?: boolean;
}): string {
  const isFavorite = shop.isFavorite === true;

  // Determine base type
  let baseType = "kringloop"; // default

  if (shop.has_horeca) {
    baseType = "kringloop_horeca";
  }

  // Add favorite suffix if applicable
  if (isFavorite) {
    return `${baseType}_favorite`;
  }

  return baseType;
}

/**
 * Get icon configuration for a shop
 * 
 * @param shop - Shop object
 * @returns MapIconConfig for use with Google Maps markers
 */
export function getIconForShop(shop: {
  has_charity?: boolean | null;
  has_horeca?: boolean | null;
  name?: string;
  isFavorite?: boolean;
}): MapIconConfig {
  const iconKey = getShopIconKey(shop);
  return mapIcons[iconKey] || mapIcons.default;
}

/**
 * Create icon configuration for regular Google Maps Marker
 * Compatible with google.maps.Marker icon property
 */
export function getMarkerIcon(shop: {
  has_charity?: boolean | null;
  has_horeca?: boolean | null;
  name?: string;
  isFavorite?: boolean;
}): google.maps.Icon | undefined {
  if (typeof window === "undefined" || !window.google?.maps?.Size) {
    return undefined;
  }

  const iconConfig = getIconForShop(shop);

  return {
    url: iconConfig.url,
    scaledSize: iconConfig.scaledSize
      ? new window.google.maps.Size(iconConfig.scaledSize.width, iconConfig.scaledSize.height)
      : undefined,
    anchor: iconConfig.anchor
      ? new window.google.maps.Point(iconConfig.anchor.x, iconConfig.anchor.y)
      : undefined,
  };
}

/**
 * Create custom HTML element for Advanced Marker
 * 
 * @param shop - Shop object
 * @returns HTMLDivElement with icon image
 */
export function createAdvancedMarkerElement(shop: {
  has_charity?: boolean | null;
  has_horeca?: boolean | null;
  name?: string;
  isFavorite?: boolean;
}): HTMLDivElement {
  const iconConfig = getIconForShop(shop);
  const size = iconConfig.scaledSize || { width: 96, height: 96 };
  const anchor = iconConfig.anchor || { x: size.width / 2, y: size.height };

  const element = document.createElement("div");
  element.style.width = `${size.width}px`;
  element.style.height = `${size.height}px`;
  element.style.display = "flex";
  element.style.alignItems = "center";
  element.style.justifyContent = "center";
  element.style.cursor = "pointer";

  // IMPORTANT: AdvancedMarkerElement anchors the BOTTOM-CENTER of the content element.
  // Our SVGs have whitespace at the bottom, so we shift the image down so the
  // tip of the teardrop aligns with the bottom of our container div.
  const translateY = size.height - anchor.y;
  const translateX = (size.width / 2) - anchor.x;

  const img = document.createElement("img");
  img.src = iconConfig.url;
  img.alt = shop.name || "Shop marker";
  img.style.width = "100%";
  img.style.height = "100%";
  img.style.objectFit = "contain";
  img.style.transform = `translate(${translateX}px, ${translateY}px)`;
  img.style.transformOrigin = "bottom center"; // Ensure scaling (on selection) pivots from the point

  element.appendChild(img);
  return element;
}

/**
 * Infer shop type from name (fallback if has_charity not available)
 * 
 * @param shopName - Shop name to analyze
 * @returns Inferred shop properties
 */
export function inferShopTypeFromName(shopName: string): {
  has_charity: boolean;
} {
  const nameLower = shopName.toLowerCase();

  // Charity keywords
  const charityKeywords = [
    "goed doel", "charity", "stichting", "foundation",
    "liefdadigheid", "non-profit"
  ];

  const hasCharity = charityKeywords.some(keyword => nameLower.includes(keyword));

  return {
    has_charity: hasCharity,
  };
}
